import React from 'react';
import Konva from 'konva';
import MainCanvasViewport from 'components/canvas/components/mini-map/components/main-canvas-viewport';
import MiniMapNodes from 'components/canvas/components/mini-map/components/mini-map-nodes';
import MiniMapMenu from 'components/canvas/components/mini-map/components/mini-map-menu';
import { Stage as KonvaStage, Layer } from 'react-konva';
import { MiniMapHolder } from 'components/canvas/components/mini-map/style';
import {
  useCanvasStageOperations,
  useCanvasStageScale,
} from 'state/canvas/stage';
import useMiniMapPositionAndScale from 'components/canvas/components/mini-map/hooks/use-mini-map-position-and-scale';
import useSetCanvasInitialPosition from 'components/canvas/components/mini-map/hooks/use-set-canvas-initial-position';
import useMiniMapOperations from 'components/canvas/components/mini-map/hooks/mini-map-operations';
import useCanvasLoading from 'components/canvas/hooks/use-canvas-loading';
import useCanvas from 'components/canvas/hooks/use-canvas';
import { usePrevious } from 'hooks';
import { useSpring } from 'react-spring';
import { filterOutNodesInPlaceholder, notEmpty, notEqual } from 'utils/js';
import { Node, SurfaceNodeUnion } from 'graphql/types';
import { useQuery } from '@apollo/client';
import { isNull } from 'lodash';
import { surfaceNodesQuery } from 'graphql/surface-node/queries/surface-nodes';
import client from 'services/apollo-client';
import { useMediaQuery } from '@mui/material';
import { isMobile } from 'react-device-detect';
import CanvasStart from 'routes/surface/components/canvas-start';
interface Props {
  surface: string;
  width: number;
  height: number;
}

const defaultProps = {
  width: 216,
  height: 162,
};

export const miniMapPadding = 0;

export default function MiniMap({ surface, width, height }: Props) {
  const miniMapLayerRef = React.useRef<Konva.Layer | any>(null);
  const miniMapStageRef = React.useRef<Konva.Stage | any>(null);

  const mainCanvasScale = useCanvasStageScale();

  const { isLoading } = useCanvasLoading();

  const variables = { input: { filter: { surface } } };

  const { data } = useQuery(surfaceNodesQuery, {
    variables,
    client,
    skip: isNull(surface),
    errorPolicy: 'none',
    fetchPolicy: 'cache-only',
  });

  const nodes = (data?.surfaceNodes?.nodes ?? []) as SurfaceNodeUnion[];

  const miniMapNodes = filterOutNodesInPlaceholder(nodes);

  useRegisterNodeOnPositionChangeListeners(miniMapNodes, miniMapStageRef);

  useSetCanvasInitialPosition(surface);

  const nodePositionChanged = useNodePositionChanged(miniMapNodes);

  const { ...positionAndScale } = useMiniMapPositionAndScale({
    width,
    height,
    miniMapStageRef,
    miniMapLayerRef,
    nodePositionChanged,
  });

  const { translateMiniMapClickPositionToCanvas } = useMiniMapOperations(
    surface,
  );

  const { updateCanvasStage } = useCanvasStageOperations(surface);

  const loadingAnimation = useSpring({
    from: { opacity: 0 },
    to: { opacity: Number(isLoading == false) },
  });

  function onStageClick(event) {
    const miniMapStage = event.target.getStage();

    const position = translateMiniMapClickPositionToCanvas(miniMapStage);

    updateCanvasStage(position);
  }

  return React.useMemo(
    () => (
      <MiniMapHolder style={loadingAnimation} width={width}>
        <KonvaStage
          ref={miniMapStageRef}
          {...positionAndScale}
          width={width}
          height={isMobile ? 0 : height}
          onClick={onStageClick}>
          <Layer ref={miniMapLayerRef} width={width} height={height}>
            <MiniMapNodes nodes={miniMapNodes} />
            {notEmpty(miniMapNodes) && <MainCanvasViewport surface={surface} />}
            <CanvasStart huge={true} />
          </Layer>
        </KonvaStage>
        <MiniMapMenu />
      </MiniMapHolder>
    ),
    [
      isLoading,
      surface,
      positionAndScale,
      nodePositionChanged,
      mainCanvasScale,
      miniMapNodes,
    ],
  );
}

MiniMap.defaultProps = defaultProps;

function useRegisterNodeOnPositionChangeListeners(
  miniMapNodes: Node[],
  miniMapStageRef,
) {
  const { findShapeById } = useCanvas();

  function findMiniMapShapeById(id: string, place?: string): any {
    // @ts-ignore
    const result = miniMapStageRef.current?.find(
      (shape) => shape.attrs.id === id,
    ) as any;

    if (Array.isArray(result)) {
      return result[0];
    }
    return result;
  }

  React.useEffect(() => {
    const surfaceNodeShapes = miniMapNodes
      .map((node) => findShapeById(node.id))
      .filter(Boolean);

    surfaceNodeShapes.forEach((nodeShape) => {
      const miniMapShape = findMiniMapShapeById(nodeShape.attrs.id);

      nodeShape?.on('xChange yChange', function () {
        miniMapShape?.position(nodeShape.position());
        miniMapStageRef.current.draw();
      });
    });

    return () => {
      surfaceNodeShapes.forEach((nodeShape) =>
        nodeShape.off('xChange yChange'),
      );
    };
  }, [miniMapStageRef.current]);
}

function useNodePositionChanged(miniMapNodes: Node[]): boolean {
  const nodesPositionMap = miniMapNodes.reduce<any>(
    (hash, node) => ({
      ...hash,
      //@ts-ignore
      [node.id]: `${node?.position?.x}${node?.position?.y}`,
    }),
    {},
  );

  const previousNodesPositionMap = usePrevious(nodesPositionMap);

  return notEqual(nodesPositionMap, previousNodesPositionMap);
}
